/**
 * A project's secrets, as this page may touch them (§5.25): which exist, who
 * last set each one and when — and a field to *write* a value, never one to
 * read it.
 *
 * The values are sealed by the Worker and handed back to a device token alone.
 * This page asks only which paths have a value; what it sends is a replacement
 * or a removal, carried by the next publish in the same transaction as the
 * document, so a document that names a secret and the value it was given arrive
 * together or not at all. A typed value is held until then and never shown
 * again — the row says it *will be replaced*, not what with.
 *
 * Secrets are not versioned: replacing a value loses the previous one. The
 * audit says who replaced it, never what it was.
 *
 * An organization that switched to end-to-end encryption seals its values on
 * its members' machines, under a key this page never holds: there is no field
 * to type one into, only the removal, and the row says where a value is set.
 */
import { useState } from 'react';
import type React from 'react';
import { describeSecretPath, secretPathsOf } from '@airlock/contracts/workspace/secrets';
import { color, font, radius } from '../../theme/tokens';
import { Badge, Input, Row, RowAction, Table } from '../../components/primitives';
import { isoDay } from '../../lib/format';
import type { WorkspaceDocument, SecretPresence, SecretWrites } from '../../lib/types';
import { actorLabel } from './shared';

export function SecretsField({
  document,
  presence,
  writes,
  onWrites,
  endToEnd = false
}: {
  document: WorkspaceDocument;
  presence: SecretPresence[];
  writes: SecretWrites;
  onWrites: (next: SecretWrites) => void;
  /** The organization seals its values end to end: set in Airlock, never here. */
  endToEnd?: boolean;
}): React.JSX.Element {
  const named = secretPathsOf(document);
  const byPath = new Map(presence.map((secret) => [secret.path, secret]));
  // A value sealed for a path the document no longer names is still offered for
  // removal: renaming a variable leaves its old value behind until somebody
  // takes it away.
  const paths = [...named, ...presence.map((secret) => secret.path).filter((path) => !named.has(path))];
  const [editing, setEditing] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const columns = 'minmax(0,1.5fr) minmax(0,1fr) 220px';

  const stage = (path: string, value: string | null): void => {
    onWrites({ ...writes, [path]: value });
    setEditing(null);
    setTyped('');
  };

  const undo = (path: string): void => {
    const next = { ...writes };
    delete next[path];
    onWrites(next);
  };

  if (paths.length === 0) {
    return (
      <p style={{ fontSize: 13.5, color: color.inkFaint, margin: 0, lineHeight: 1.6 }}>
        No secrets. Set an environment variable&apos;s kind to <strong style={{ fontWeight: 600 }}>Secret</strong>, or mark a
        console variable secret, and its value is sealed apart from the document.
      </p>
    );
  }

  return (
    <Table columns={columns} header={['Secret', 'Value', '']}>
      {paths.map((path) => {
        const pending = Object.prototype.hasOwnProperty.call(writes, path) ? writes[path] : undefined;
        const held = byPath.get(path);
        const orphan = !named.has(path);
        return (
          <Row key={path} columns={columns}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: font.mono, fontSize: 12.5, wordBreak: 'break-all' }}>
                {describeSecretPath(document, path)}
              </div>
              {orphan && (
                <div style={{ fontSize: 12, color: color.warnText, marginTop: 3 }}>
                  The document no longer names this — remove its value.
                </div>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              {pending === null ? (
                <Badge tone="bad">will be removed</Badge>
              ) : typeof pending === 'string' ? (
                <Badge tone="warn">will be replaced</Badge>
              ) : held ? (
                <span style={{ fontSize: 13, color: color.inkSubtle }}>
                  set {isoDay(held.updatedAt)} by {actorLabel(held.updatedBy)}
                </span>
              ) : (
                <Badge tone="off">empty</Badge>
              )}
              {editing === path && (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (typed) stage(path, typed);
                  }}
                  style={{ display: 'flex', gap: 8, marginTop: 10 }}
                >
                  <Input
                    type="password"
                    autoComplete="off"
                    aria-label={`New value for ${describeSecretPath(document, path)}`}
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                    style={{ fontFamily: font.mono, fontSize: 13, padding: '8px 10px', borderRadius: radius.md }}
                  />
                  <RowAction onClick={() => typed && stage(path, typed)}>Keep</RowAction>
                </form>
              )}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {pending !== undefined ? (
                <RowAction onClick={() => undo(path)}>Undo</RowAction>
              ) : (
                <>
                  {!orphan && endToEnd && !held && (
                    <span style={{ fontSize: 12.5, color: color.inkFaint }}>Set it in Airlock</span>
                  )}
                  {!orphan && !endToEnd && (
                    <RowAction
                      onClick={() => {
                        setEditing(editing === path ? null : path);
                        setTyped('');
                      }}
                    >
                      {editing === path ? 'Cancel' : held ? 'Replace' : 'Set'}
                    </RowAction>
                  )}
                  {held && (
                    <RowAction danger onClick={() => stage(path, null)}>
                      Remove
                    </RowAction>
                  )}
                </>
              )}
            </div>
          </Row>
        );
      })}
    </Table>
  );
}
